import { RefObject, useLayoutEffect } from "react"

type KeyboardModifiers = {
  alt?: boolean
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
}

type Hotkey = { keys?: readonly string[]; modifiers: KeyboardModifiers }

type HotkeyOptions<T extends HTMLElement = HTMLElement> = {
  ref?: RefObject<T | null>
  combinationKey?: string
}

const reservedModifierKeywords = ["ctrl", "control", "unknown", "meta", "alt", "shift", "os"]
const specialKeys: Record<string, string> = {
  esc: "escape",
  return: "enter",
  ".": "period",
  ",": "comma",
  "-": "slash",
  " ": "space",
  "`": "backquote",
  "#": "backslash",
  "+": "bracketright",
  ShiftLeft: "shift",
  ShiftRight: "shift",
  AltLeft: "alt",
  AltRight: "alt",
  MetaLeft: "meta",
  MetaRight: "meta",
  OSLeft: "meta",
  OSRight: "meta",
  ControlLeft: "ctrl",
  ControlRight: "ctrl",
}

function mapKey(key: string): string {
  return (specialKeys[key] || key || "")
    .trim()
    .toLowerCase()
    .replace(/key|digit|numpad|arrow/, "")
}

function parseHotkey(hotkey: string, combinationKey = "+"): Hotkey {
  const keys = hotkey
    .toLocaleLowerCase()
    .split(combinationKey)
    .map((k) => mapKey(k))

  const modifiers: KeyboardModifiers = {
    alt: keys.includes("alt"),
    ctrl: keys.includes("ctrl") || keys.includes("control"),
    shift: keys.includes("shift"),
    meta: keys.includes("meta"),
  }

  const singleCharKeys = keys.filter((k) => !reservedModifierKeywords.includes(k))

  return { modifiers, keys: singleCharKeys }
}

const isMatchModifiersKey = (event: KeyboardEvent, modifiers: KeyboardModifiers) => {
  const { ctrlKey, altKey, metaKey, shiftKey } = event
  const { alt, ctrl, meta, shift } = modifiers
  return alt === altKey && ctrl === ctrlKey && meta === metaKey && shift === shiftKey
}

export const useHotkeys = <T extends HTMLElement = HTMLElement>(
  hotkey: string,
  callback: () => void,
  options?: HotkeyOptions<T>,
) => {
  const { ref, combinationKey = "+" } = options ?? {}

  useLayoutEffect(() => {
    const { keys = [], modifiers } = parseHotkey(hotkey, combinationKey)
    const handleKeyEvent = (event: Event) => {
      if (!(event instanceof KeyboardEvent) || event.key === undefined) {
        // Not a keyboard event or synthetic event (e.g., Chrome autofill). Ignore.
        return
      }
      const keyCode = mapKey(event.code)

      if (keys.includes(keyCode) && isMatchModifiersKey(event, modifiers)) {
        callback()
      }
    }

    const target = ref?.current ?? window
    target.addEventListener("keydown", handleKeyEvent)
    return () => target.removeEventListener("keydown", handleKeyEvent)
  }, [hotkey, combinationKey, callback, ref])
}
